import type { Entitlements } from '~/enums/entitlements';

export const MODEL_ARTISTS = 'artists';
export const MODEL_PRODUCTION_HOUSES= 'productionHouses';
export const MODEL_VENUES = 'venues';
export const MODEL_RESERVATIONS = 'reservations';
export const MODEL_EVENTS = 'events';

export type FollowModelType = typeof MODEL_ARTISTS | typeof MODEL_PRODUCTION_HOUSES | typeof MODEL_VENUES;

export const USER_STORE_DATA_KEY_USER = 'user';
export const USER_STORE_DATA_KEY_TOKEN = 'token';

export const USER_STORE_DATA_KEY_USER_ARTISTS = 'user-artists';
export const USER_STORE_DATA_KEY_USER_VENUES = 'user-venues';
export const USER_STORE_DATA_KEY_USER_PRODUCTION_HOUSES = 'user-production-houses';
export const USER_STORE_DATA_KEY_USER_EVENTS = 'user-events';
export const USER_STORE_DATA_KEY_USER_RESERVATIONS = 'user-reservations';
export const USER_STORE_DATA_KEY_USER_WAITING_LISTS = 'user-waiting-lists';

export const DATA_KEY_USER = 'user';
export const DATA_KEY_TOKEN = 'token';

export const DATA_KEY_USER_ARTISTS = 'user-artists';
export const DATA_KEY_USER_VENUES = 'user-venues';
export const DATA_KEY_USER_PRODUCTION_HOUSES = 'user-production-houses';
export const DATA_KEY_USER_EVENTS = 'user-events';

export const USER_STORE_DATA_KEY_USER_FOLLOWS: Record<FollowModelType, string> = {
    [MODEL_ARTISTS]: USER_STORE_DATA_KEY_USER_ARTISTS,
    [MODEL_PRODUCTION_HOUSES]: USER_STORE_DATA_KEY_USER_PRODUCTION_HOUSES,
    [MODEL_VENUES]: USER_STORE_DATA_KEY_USER_VENUES,
};


export const useUserStore = defineStore('user', () => {
    const user = ref<null|User>(null);
    const jwtToken = ref<null|string>();

    const followingArtists = ref<number[]>([]);
    const followingProductionHouses = ref<number[]>([]);
    const followingVenues = ref<number[]>([]);
    const savedEvents = ref<number[]>([]);
    const reservations = ref<number[]>([]);
    const waitingList = ref<number[]>([]);

    const loggedIn = computed(() => !!user.value);

    const canMakeReservation = computed(() => !!user.value?.canMakeReservation);
    const canMakeFreePremiumReservation = computed(() => !!user.value?.canMakeFreePremiumReservation);

    function setUser(payload: User): void {
        user.value = payload;
    }

    function hasReservation(eventDateId: number): boolean {
        return reservations.value.includes(eventDateId);
    }

    function isInWaitingList(eventDateId: number): boolean {
        return waitingList.value.includes(eventDateId);
    }

    function followsModel(modelId: number, modelType: FollowModelType): boolean {
        if (modelType === MODEL_ARTISTS) {
            return followingArtists.value.includes(modelId);
        }

        if (modelType === MODEL_PRODUCTION_HOUSES) {
            return followingProductionHouses.value.includes(modelId);
        }

        if (modelType === MODEL_VENUES) {
            return followingVenues.value.includes(modelId);
        }

        return false;
    }

    async function fetchToken() {
        const { setCookies, unsetCookies } = useJwtCookies();

        const { data, error, status } = await useAsyncFetchLaravel<{ token: string }>(
            '/v1.0/auth/jwt',
            {
                method: 'PUT',
                key: USER_STORE_DATA_KEY_TOKEN,
            }
        );

        watchEffect(() => {
            if (status.value === 'error' && error.value) {
                jwtToken.value = null;

                unsetCookies();
            }

            if (data.value?.token) {
                jwtToken.value = data.value.token;

                setCookies(jwtToken.value);
            }
        });
    }

    async function fetchUser() {
        const { data, error, status } = await useAsyncFetchLaravel<User>(
            '/v2.0/user/me',
            {
                key: USER_STORE_DATA_KEY_USER
            }
        );

        watchEffect(() => {
            if (status.value === 'error' && error.value) {
                throw new Error('Invalid session: failed to fetch user');
            }

            user.value = data.value;
        });
    }


    async function fetchFollowsArtists() {
        const { data, error, status } = await useAsyncFetchLaravel<number[]>('/v2.0/user/me/artist', {
            key: USER_STORE_DATA_KEY_USER_ARTISTS,
            lazy: true,
        });

        watchEffect(() => {
            if (status.value === 'error' && error.value) {
                console.error('Failed to fetch user saved artists');
            }

            if (data.value) {
                followingArtists.value = data.value;
            }
        });
    }

    async function fetchFollowsProductionHouses() {
        const { data, error, status } = await useAsyncFetchLaravel<number[]>('/v2.0/user/me/production-house', {
            key: USER_STORE_DATA_KEY_USER_PRODUCTION_HOUSES,
            lazy: true,
        });

        watchEffect(() => {
            if (status.value === 'error' && error.value) {
                console.error('Failed to fetch user saved productionHouses');
            }

            if (data.value) {
                followingProductionHouses.value = data.value;
            }
        });
    }

    async function fetchFollowsVenues() {
        const { data, error, status } = await useAsyncFetchLaravel<number[]>('/v2.0/user/me/venue', {
            key: USER_STORE_DATA_KEY_USER_VENUES,
            lazy: true,
        });

        watchEffect(() => {
            if (status.value === 'error' && error.value) {
                console.error('Failed to fetch user saved venues');
            }

            if (data.value) {
                followingVenues.value = data.value;
            }
        });
    }

    async function fetchReservations() {
        const { data, error, status } = await useAsyncFetchLaravel<number[]>('/v2.0/user/me/reservations/ids', {
            key: USER_STORE_DATA_KEY_USER_RESERVATIONS,
            lazy: true,
        });

        watchEffect(() => {
            if (status.value === 'error' && error.value) {
                console.error(error.value);
                console.error('Failed to fetch user reservations');
            }

            if (data.value) {
                reservations.value = data.value;
            }
        });
    }

    async function fetchSavedEvents() {
        const { data, status, error } = await useAsyncFetchLaravel<number[]>('/v2.0/user/me/favorite', {
            key: USER_STORE_DATA_KEY_USER_EVENTS,
            lazy: true,
        });

        watchEffect(() => {
            if (status.value === 'error' && error.value) {
                console.error('Failed to fetch user saved events');
            }

            if (data.value) {
                savedEvents.value = data.value;
            }
        });
    }

    async function fetchWaitingLists() {
        const { data, error, status } = await useAsyncFetchLaravel<number[]>('/v2.0/user/me/waiting-list/ids', {
            key: USER_STORE_DATA_KEY_USER_WAITING_LISTS,
            lazy: true,
        });

        watchEffect(() => {
            if (status.value === 'error' || error.value) {
                console.error(error.value);
                console.error('Failed to fetch users waiting list');
            }

            if (data.value) {
                waitingList.value = data.value;
            }
        });
    }

    const logout = () => {
        user.value = null;
        jwtToken.value = null;
    };

    function fetchUserRelations() {
        if (jwtToken.value) {
            return Promise.all([
                fetchUser(),
                fetchSavedEvents(),
                fetchFollowsArtists(),
                fetchFollowsProductionHouses(),
                fetchReservations(),
                fetchFollowsVenues(),
                fetchWaitingLists()
            ]);
        }
    }

    const can = (entitlement: Entitlements) => {
        const e = user
            .value
            ?.entitlements
            ?.find(({ name }) => name === entitlement);

        return e?.value ?? false;
    };

    return {
        user,
        jwtToken,

        followingArtists,
        followingProductionHouses,
        followingVenues,
        savedEvents,
        reservations,
        waitingList,

        loggedIn,
        canMakeReservation,
        canMakeFreePremiumReservation,

        setUser,
        hasReservation,
        isInWaitingList,
        followsModel,
        fetchToken,
        fetchUser,
        fetchUserRelations,
        logout,

        can
    };
});
